import { getEcState } from '@packages/utilities';
import { getMainAudience } from '@packages/cms-components';
import { decodeFromBase64 } from '@packages/cms-components/src/utils/decodeFromBase64';
import { fetchContentSnippetData } from './fetchContentSnippetData';
import { fetchPromoData } from './fetchPromoData';

/**
 * fetch, filter and cleanup promotion data
 *
 * @param query holds different filter options by query params
 * @param cookies holds cookies to also filter information on
 * @param locale defines the language for the returned data
 * @returns data for shop (by page context query param),
 * selected (by query param BonusNumber) and
 * active (by cookie value) promotions
 */
export const promotionBannerDataFetcher = async (
  query: {
    shopId?: string;
    BonusNumber?: string;
    P1?: string;
    TestDate?: string;
    ParentCode?: string;
    platform?: 'app' | 'web';
  },
  cookies: {
    bonuscode?: string;
    ecState?: string;
    textsBonusCode?: string;
  },
  locale?: string,
  callOwnApi?: boolean,
) => {
  const { shopId = '-1', BonusNumber, P1, TestDate: testDate, ParentCode, platform } = query;

  const audience = getMainAudience(getEcState(cookies));

  const fetchedShopPromotionData = await fetchContentSnippetData(
    {
      shopId,
      locale,
      testDate,
      audience,
      platform,
    },
    callOwnApi,
  );

  const selectedBonusNumber = BonusNumber || (P1 && decodeFromBase64(P1)) || undefined;

  let fetchedSelectedPromotionData;
  try {
    fetchedSelectedPromotionData =
      selectedBonusNumber &&
      (await fetchPromoData(
        { cart: selectedBonusNumber, texts: ParentCode || selectedBonusNumber },
        locale,
        callOwnApi,
      ));
  } catch {
    // do nothing - leave data as undefined
  }

  let fetchedActivePromotionData;
  try {
    fetchedActivePromotionData =
      cookies?.bonuscode &&
      (await fetchPromoData(
        { cart: cookies.bonuscode, texts: cookies.textsBonusCode || cookies.bonuscode },
        locale,
        callOwnApi,
      ));
  } catch {
    // do nothing - leave data as undefined
  }

  // I need to omit the complete prop if there is no value to prevent error: "'undefined' cannot be seralized as JSON"
  return {
    ...(fetchedShopPromotionData?.[0] && { shop: fetchedShopPromotionData?.[0] }),
    ...(fetchedSelectedPromotionData && { selected: fetchedSelectedPromotionData }),
    ...(fetchedActivePromotionData && { active: fetchedActivePromotionData }),
  };
};
